import { CloseButton } from "./close-button";
import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import appIcon from "@/assets/app-icon-128.webp";
import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSideNav } from "@/hooks/use-media-query";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * The card the app asks questions with — a little drawer on a phone, a
 * floating card on a tablet or desk. Ported from PropPay LX.
 *
 * Every moment this app wants something from the reader wears this: take the
 * new build, install the app, turn on notifications, add a passkey. They were
 * a toast, two hand-rolled bottom cards and an in-page banner, which meant an
 * update notice looked identical to an error and a passkey offer looked like
 * nothing at all — and a reader who has learned that our nudges are noise
 * dismisses the one that mattered without reading it.
 *
 * ── What makes it read as an offer rather than a notice ──
 *
 * A 44px mark to anchor it, and the answer as a full-width button with the
 * refusal on its own line BENEATH rather than beside it. Beside, the two
 * compete for the same glance; underneath, the eye lands on what we are
 * proposing and then finds the way out.
 *
 * One on screen at a time — lib/nudgeQueue.ts decides which. That queue
 * replaced the `data-bottom-card` / `--bottom-card-h` stacking contract
 * UpdatePrompt and NotificationPermissionPrompt used to share: two cards that
 * could not see each other, stacked one above the other by a measured height.
 * Now they cannot be on screen together at all.
 */

export interface NudgeAction {
  label: string;
  onClick: () => void;
  /** Disables the button (a request in flight) without changing its label's slot. */
  busy?: boolean;
}

export interface NudgeStep {
  icon: LucideIcon;
  /** The sentence, with the control's own name picked out by `emphasis`. */
  text: string;
  emphasis: string;
}

export type NudgeTone = "default" | "alert";

/** The 44px glyph tile, for a nudge about something INSIDE the app. */
export function NudgeTile({ icon: Icon, tone = "default" }: { icon: LucideIcon; tone?: NudgeTone }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-11 shrink-0 items-center justify-center rounded-full border",
        tone === "alert"
          ? "border-destructive/25 bg-destructive/10 text-destructive"
          : "border-primary/20 bg-primary/[.12] text-primary-ink",
      )}
    >
      <Icon className="size-5" strokeWidth={2} />
    </span>
  );
}

/** The app's own icon, for the install card: what the reader will look for on their Home Screen. */
export function NudgeAppIcon() {
  return (
    <img
      src={appIcon}
      alt=""
      width={44}
      height={44}
      onError={(e) => {
        e.currentTarget.hidden = true;
      }}
      className="size-11 shrink-0 rounded shadow-xs"
    />
  );
}

/** The step's sentence, with the control's own name picked out of it. */
function StepText({ text, emphasis }: { text: string; emphasis: string }) {
  const at = text.indexOf(emphasis);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="font-semibold text-foreground">{emphasis}</span>
      {text.slice(at + emphasis.length)}
    </>
  );
}

/** Past this, a release is "Not now" — the dialog sheet's own feel, shorter for a shorter panel. */
const DISMISS_DISTANCE = 80;
/** A flick counts at this speed (px/s) once it has travelled this far. */
const FLICK_SPEED = 600;
const FLICK_DISTANCE = 30;

export function NudgeCard({
  /** The 44px mark: `NudgeAppIcon` for install, a `NudgeTile` for everything else. */
  lead,
  title,
  body,
  steps,
  hint,
  primary,
  secondary,
  /**
   * Omitted where there is nothing to refuse — a card that is a fact about the
   * session rather than an offer (signed out) has no "Not now", no ✕ and no
   * drag handle.
   */
  onClose,
  tone = "default",
  /** Accessible name for the region. Defaults to the title. */
  label,
  /** `inline` renders the card in flow — for the gallery and for previews. */
  placement = "fixed",
  testId,
}: {
  lead: ReactNode;
  title: string;
  body: ReactNode;
  steps?: NudgeStep[];
  hint?: string;
  primary?: NudgeAction;
  secondary?: NudgeAction;
  onClose?: () => void;
  tone?: NudgeTone;
  label?: string;
  placement?: "fixed" | "inline";
  testId?: string;
}) {
  // The navigation split (SIDE_NAV_QUERY): a drawer where there is a tab bar
  // (a phone, an upright iPad), a floating card where there is a rail.
  const t = useTranslation();
  const phone = !useSideNav();

  const surface = tone === "alert" ? "alert-surface" : "surface";

  const content = (
    <>
      <div className="flex items-start gap-3">
        {lead}
        <div className="min-w-0 flex-1">
          <p className={cn("text-label font-bold leading-snug", tone === "alert" && "text-on-tint-destructive")}>{title}</p>
          <p className="mt-0.5 text-caption leading-snug text-muted-foreground">{body}</p>
        </div>
        {onClose && (
          <CloseButton
            label={t("uiNotNow", "Not Now")}
            onClick={onClose}
            className="-mt-2"
            data-testid={testId ? `${testId}-close` : undefined}
          />
        )}
      </div>

      {steps && steps.length > 0 && (
        <ol className="mt-3 space-y-2 rounded-md bg-muted/60 px-3 py-2.5">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <li key={s.text} className="flex items-center gap-2 text-caption leading-snug text-muted-foreground">
                <span
                  aria-hidden="true"
                  className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-primary text-micro font-bold text-primary-foreground"
                >
                  {i + 1}
                </span>
                <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full border border-border bg-card">
                  <Icon className="size-3.5 text-primary-ink" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <StepText text={s.text} emphasis={s.emphasis} />
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {(primary || secondary) && (
        <div className="mt-3 flex flex-col gap-1">
          {primary && (
            <button
              type="button"
              onClick={primary.onClick}
              disabled={primary.busy}
              data-testid={testId ? `${testId}-primary` : undefined}
              className="min-h-11 touch:min-h-touch w-full rounded-full bg-primary px-4 text-label font-bold text-primary-foreground transition-[background-color,box-shadow] duration-instant ease-standard hover:shadow-md disabled:cursor-default disabled:opacity-70"
            >
              {primary.label}
            </button>
          )}
          {secondary && (
            <button
              type="button"
              onClick={secondary.onClick}
              disabled={secondary.busy}
              data-testid={testId ? `${testId}-secondary` : undefined}
              className="min-h-10 touch:min-h-touch w-full rounded-full px-4 text-label font-semibold text-muted-foreground transition-colors duration-instant ease-standard hover:bg-foreground/[.05]"
            >
              {secondary.label}
            </button>
          )}
        </div>
      )}

      {hint && <p className="mt-2 text-micro leading-snug text-muted-foreground">{hint}</p>}
    </>
  );

  if (placement === "inline") {
    return (
      <div data-testid={testId} className={cn(surface, "float mx-auto w-full max-w-md rounded-lg p-3.5")}>
        {content}
      </div>
    );
  }

  if (phone) {
    return (
      <NudgeDrawer onClose={onClose} label={label ?? title} surface={surface} testId={testId}>
        {content}
      </NudgeDrawer>
    );
  }

  return (
    <div
      /*
        A DESK OR TABLET: a floating card, bottom-centre. The app's two kinds of
        notice split by position — a toast says something just happened and goes
        away by itself; a card, down here, asks the reader to do something and
        waits.

        Centred on the CONTENT column, not the window: `--sidebar-space` is the
        docked sidebar's width plus its gap (published by AppShell; 0 where
        there is none, as on sign-in). The strip is pointer-events-none — it
        spans the width so the card can centre in it, and must not swallow
        clicks on the page either side.

        Layer: above the app chrome, below the modal scrim — opening a dialog
        covers a waiting nudge rather than the nudge covering the dialog's
        buttons.
      */
      className={cn(
        "pointer-events-none fixed bottom-[max(1.25rem,var(--sa-bottom))] left-[var(--sidebar-space,0px)] right-0 z-banner flex justify-center px-3 print:hidden",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2",
      )}
      role="dialog"
      aria-label={label ?? title}
      data-nudge=""
      data-testid={testId}
    >
      <div className={cn(surface, "float pointer-events-auto w-full max-w-md rounded-lg p-3.5")}>{content}</div>
    </div>
  );
}

/**
 * A PHONE: a little drawer, the same object every dialog becomes there.
 *
 * It rises from the bottom edge with the sheet's rounded top and grab handle
 * and covers the tab bar while it is up, as a dialog sheet does. What it does
 * NOT take from the dialogs is the scrim: a sheet with a scrim means "answer
 * this now", and these wait until the reader is ready, so the page above stays
 * live.
 *
 * The handle drags it, and a pull past the threshold (or a flick) is "Not
 * now". A tap on the handle that never moved is "Not now" too, so the handle
 * works without the gesture. It is only drawn when there IS a "Not now". Plain
 * pointer events, as the dialog sheet uses: framer's drag feature is not in
 * the lazily-loaded motion bundle (lib/motionFeatures.ts), and pulling it in
 * for one handle would put the engine back on the static path.
 */
function NudgeDrawer({
  children,
  onClose,
  label,
  surface,
  testId,
}: {
  children: ReactNode;
  onClose?: () => void;
  label: string;
  surface: string;
  testId?: string;
}) {
  const [dragY, setDragY] = useState(0);
  const from = useRef<{ y: number; t: number } | null>(null);
  const last = useRef<{ y: number; t: number } | null>(null);
  const moved = useRef(false);

  const onDown = (e: ReactPointerEvent) => {
    from.current = { y: e.clientY, t: e.timeStamp };
    last.current = from.current;
    moved.current = false;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e: ReactPointerEvent) => {
    if (!from.current) return;
    const dy = e.clientY - from.current.y;
    if (Math.abs(dy) > 4) moved.current = true;
    last.current = { y: e.clientY, t: e.timeStamp };
    setDragY(Math.max(0, dy));
  };
  const onUp = (e: ReactPointerEvent) => {
    if (!from.current) return;
    const start = from.current;
    const prev = last.current ?? start;
    from.current = null;
    const dy = Math.max(0, e.clientY - start.y);
    const dt = Math.max(1, e.timeStamp - prev.t);
    const speed = ((e.clientY - prev.y) / dt) * 1000;
    if (dy > DISMISS_DISTANCE || (speed > FLICK_SPEED && dy > FLICK_DISTANCE)) {
      onClose?.();
      return;
    }
    setDragY(0);
  };

  return (
    <div
      role="dialog"
      aria-label={label}
      data-nudge=""
      data-testid={testId}
      style={dragY ? { transform: `translate3d(0, ${dragY}px, 0)`, animationName: "none", transition: "none" } : undefined}
      className={cn(
        surface,
        "float fixed inset-x-0 bottom-0 z-banner rounded-t-xl rounded-b-none border-b-0 print:hidden",
        "pl-[max(1rem,var(--sa-left))] pr-[max(1rem,var(--sa-right))] pb-[max(1rem,var(--sa-bottom))]",
        "transition-transform duration-fast ease-standard",
        "motion-safe:animate-in motion-safe:slide-in-from-bottom motion-safe:duration-300",
        onClose ? "pt-1" : "pt-4",
      )}
    >
      {onClose && (
        <button
          type="button"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={() => {
            from.current = null;
            setDragY(0);
          }}
          onClick={() => {
            // A press that never moved: close, the same as the gesture.
            if (!moved.current) onClose();
          }}
          // The gesture's handle, for a thumb. Not in the tab order and not
          // announced: the ✕ beside the title is the same "Not now" for a
          // keyboard or a screen reader, and two controls by one name is noise.
          tabIndex={-1}
          aria-hidden="true"
          className="hit-target mx-auto -mb-1 flex w-40 cursor-grab touch-none justify-center pb-2 pt-2 outline-hidden active:cursor-grabbing"
        >
          <span className="block h-1 w-9 rounded-hair bg-foreground/20" />
        </button>
      )}
      <div className="mx-auto max-w-md">{children}</div>
    </div>
  );
}
